import { styled } from '@mui/material/styles';
import { Box, Container, Typography, Grid, Skeleton } from '@mui/material';
import { motion, useInView, useMotionValue, useSpring } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { usePlatformStats } from '../hooks/usePlatformStats';
import { venueTypeColors } from '../../../theme/venueTypeColors';
import { VenueType } from '../constants/venueTypes';

const StatsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(12, 0),
  background: theme.palette.background.default,
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 0),
  },
}));

const StatBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
}));

const StatNumber = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color: string }>(({ color }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontWeight: 700,
  fontSize: '3.5rem',
  lineHeight: 1,
  color: color,
  marginBottom: 8,
  textShadow: `0 0 30px ${color}50`,
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '1.125rem',
  color: theme.palette.text.secondary,
  fontWeight: 500,
}));

interface AnimatedCounterProps {
  value: number;
  color: string;
  suffix?: string;
}

function AnimatedCounter({ value, color, suffix = '' }: AnimatedCounterProps) {
  const ref = useRef<HTMLDivElement>(null);
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, { duration: 2000 });
  const isInView = useInView(ref, { once: true, amount: 0.5 });

  useEffect(() => {
    if (isInView) {
      motionValue.set(value);
    }
  }, [motionValue, isInView, value]);

  useEffect(() => {
    springValue.on('change', (latest) => {
      if (ref.current) {
        ref.current.textContent = `${Math.floor(latest).toLocaleString('en-IN')}${suffix}`;
      }
    });
  }, [springValue, suffix]);

  return (
    <StatNumber ref={ref} color={color}>
      0{suffix}
    </StatNumber>
  );
}

export function StatsCounter() {
  const { data: stats, isLoading } = usePlatformStats();

  const statsData = [
    {
      value: stats?.totalVenues ?? 0,
      label: 'Venues',
      suffix: '+',
      color: venueTypeColors[VenueType.GAMING].primary,
    },
    {
      value: stats?.totalCities ?? 0,
      label: 'Cities',
      suffix: '+',
      color: venueTypeColors[VenueType.SNOOKER].primary,
    },
    {
      value: stats?.totalBookings ?? 0,
      label: 'Happy Bookings',
      suffix: '+',
      color: venueTypeColors[VenueType.KIDS_PLAYAREA].primary,
    },
  ];

  return (
    <StatsContainer>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {statsData.map((stat, index) => (
            <Grid size={{ xs: 12, md: 4 }} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <StatBox>
                  {isLoading ? (
                    <>
                      <Skeleton variant="text" width={120} height={56} sx={{ mx: 'auto', mb: 1 }} />
                      <Skeleton variant="text" width={80} sx={{ mx: 'auto' }} />
                    </>
                  ) : (
                    <>
                      <AnimatedCounter value={stat.value} color={stat.color} suffix={stat.suffix} />
                      <StatLabel>{stat.label}</StatLabel>
                    </>
                  )}
                </StatBox>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </StatsContainer>
  );
}
